import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import cl from '@/screensComponents/Form/Form.module.scss'
import { FormStatus } from '@/screensComponents/Form/FormStatus/FormStatus'

import { emailSchemaOptional } from '@/shared/lib/validation/email'
import { phoneSchema } from '@/shared/lib/validation/phone'
import { Button, Input, RHFEmailInput, RHFPhoneInput } from '@/shared/ui'

import { usePostInsuranceFormMutation } from '../../api/insurance.api'

const formSchema = z.object({
	NAME: z
		.string()
		.min(1, 'Введите имя и фамилию')
		.regex(/^[а-яА-ЯёЁa-zA-Z]+(\s[а-яА-ЯёЁa-zA-Z]+)?$/, 'Введите имя и фамилию'),
	PHONE: phoneSchema,
	EMAIL: emailSchemaOptional,
	COMPANY_TITLE: z
		.string()
		.optional()
		.refine(
			val => !val || /^[а-яА-ЯёЁa-zA-Z]/.test(val),
			'Проверьте название организации, например: ООО Название организации'
		)
})

type FormInput = z.input<typeof formSchema>
type FormOutput = z.output<typeof formSchema>

export const InsuranceForm = () => {
	const [postForm, { isLoading, isSuccess }] = usePostInsuranceFormMutation()

	const { control, handleSubmit } = useForm<FormInput, unknown, FormOutput>({
		mode: 'onBlur',
		resolver: zodResolver(formSchema),
		defaultValues: { NAME: '', PHONE: '', EMAIL: '', COMPANY_TITLE: '' }
	})

	const onSubmit: SubmitHandler<FormOutput> = data => {
		postForm({
			NAME: data.NAME,
			PHONE: data.PHONE,
			EMAIL: data.EMAIL ?? undefined,
			COMPANY_TITLE: data.COMPANY_TITLE || undefined
		})
	}

	if (isSuccess || isLoading) {
		return <FormStatus isSuccess={isSuccess} isLoading={isLoading} onSuccessContent={undefined} />
	}

	return (
		<div className={cl.main}>
			<div className={cl.textWrapper}>
				<h1 className={cl.title}>Заявка на страхование устройств</h1>
				<p className={cl.subtitle}>
					Оставьте заявку, и мы свяжемся с вами. Проконсультируем и подберём программу страхования устройств.
				</p>
			</div>
			<form className={cl.form} onSubmit={handleSubmit(onSubmit)}>
				<div className={cl.inputsWrapper}>
					<div className={cl.row}>
						<Controller
							control={control}
							name="NAME"
							render={({ field: { value, onChange, onBlur, ref }, fieldState: { error, invalid } }) => (
								<Input
									ref={ref}
									name="name"
									label="Имя, фамилия"
									value={value}
									helperText={error?.message}
									onChange={onChange}
									onBlur={onBlur}
									onClear={() => onChange('')}
									isError={invalid}
									fullWidth
								/>
							)}
						/>
					</div>
					<div className={cl.row}>
						<RHFPhoneInput name="PHONE" control={control} fullWidth />
						<RHFEmailInput name="EMAIL" control={control} required={false} fullWidth />
					</div>
					<div className={cl.row}>
						<Controller
							control={control}
							name="COMPANY_TITLE"
							render={({ field: { value, onChange, onBlur, ref }, fieldState: { error, invalid } }) => (
								<Input
									ref={ref}
									name="company-title"
									label="Название организации"
									value={value ?? ''}
									helperText={error?.message}
									onChange={onChange}
									onBlur={onBlur}
									onClear={() => onChange('')}
									isError={invalid}
									fullWidth
								/>
							)}
						/>
					</div>
				</div>
				<div className={cl.btnWrapper}>
					<Button type="submit" className={cl.btn} disabled={isLoading}>
						<p className={cl.btnText}>Отправить заявку</p>
					</Button>
					<p className={cl.notation}>
						Нажимая кнопку, я подтверждаю свое ознакомление и согласие с{' '}
						<Link prefetch={false} href="https://cdn.iport.ru/iport/pdf/Politika_konfidecialnosti_PT_sajt_IPort.pdf">
							Политикой Конфиденциальности
						</Link>
						{', '}
						<Link prefetch={false} href="https://cdn.iport.ru/iport/pdf/soglasie_IPort_.pdf">
							Согласием на обработку персональных данных
						</Link>
						. Я&nbsp;достиг(ла) 18-летнего возраста.
					</p>
				</div>
			</form>
		</div>
	)
}
